import React, { useState, useEffect } from "react";
import { Plus, Trash2, Download, Upload, Edit2 } from "lucide-react";
import {
  generateId,
  formatCost,
  ITEM_CATEGORIES,
} from "./helpers";
import { PremadeTemplatesSection } from "./components/PremadeTemplatesSection";
import { HOMEBREW_SETS } from "./data/homebrewSets";
import { GENERAL_ARMOURY_ITEMS } from "./data/vanillaTemplates";
import { FACTIONS } from "./data/premadeTemplates";
import { ModelTemplate, ItemTemplate } from "./types";

import { Warband, Item, Model } from "./types";

interface TemplatesTabProps {
  warband: Warband;
  setWarband: (warband: Warband) => void;
  modelTemplates: ModelTemplate[];
  itemTemplates: ItemTemplate[];
  updateModelTemplates: (templates: ModelTemplate[]) => void;
  updateItemTemplates: (templates: ItemTemplate[]) => void;
  onAddModel: (data: { model: Model; newItems: Item[] }) => void;
  items: Item[];
  setItems: (items: Item[]) => void;
  models: Model[];
  openModelTemplateEditor: (template?: ModelTemplate) => void;
  openItemTemplateEditor: (template?: ItemTemplate) => void;
}

const ModelTemplateCard: React.FC<{
  template: ModelTemplate;
  itemTemplates: ItemTemplate[];
  onDelete: (id: string) => void;
  onEdit: (template: ModelTemplate) => void;
}> = ({ template, itemTemplates, onDelete, onEdit }) => {
  const templateItems = itemTemplates.filter((it) =>
    template.itemTemplateIds?.includes(it.id)
  );

  let totalDucatCost = template.costCurrency === "ducats" ? template.cost : 0;
  let totalGloryCost = template.costCurrency === "glory" ? template.cost : 0;
  templateItems.forEach((item) => {
    if (item.costCurrency === "ducats") totalDucatCost += item.cost;
    if (item.costCurrency === "glory") totalGloryCost += item.cost;
  });

  const isElite = template.type === "elite";

  return (
    <div
      className={`relative bg-gray-900/90 rounded border border-gray-700 p-3 hover:shadow-lg hover:scale-105 shadow-lg cursor-pointer ${
        isElite ? "ring-1 ring-yellow-600/30" : ""
      }`}
    >
      <div className="absolute top-1 right-1 flex gap-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit(template);
          }}
          className="p-1 bg-red-900/50 rounded hover:bg-red-900/70 text-red-300 border border-red-800"
        >
          <Edit2 size={14} />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (confirm("Delete this template?")) {
              onDelete(template.id);
            }
          }}
          className="p-1 bg-red-900 rounded hover:bg-red-800 text-white"
        >
          <Trash2 size={14} />
        </button>
      </div>

      <div className="mb-2">
        <h4
          className={`text-sm font-bold ${
            isElite ? "text-yellow-400" : "text-white"
          }`}
        >
          {template.templateName}
        </h4>
        <p className="text-xs text-gray-400">
          {template.name} • {template.class}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-2 text-xs">
        {totalDucatCost > 0 && (
          <span className="text-yellow-400 font-semibold">
            {totalDucatCost}👑
          </span>
        )}
        {totalGloryCost > 0 && (
          <span className="text-blue-400 font-semibold">{totalGloryCost}☼</span>
        )}
      </div>
    </div>
  );
};

const ItemTemplateCard: React.FC<{
  template: ItemTemplate;
  onDelete: (id: string) => void;
  onEdit: (template: ItemTemplate) => void;
}> = ({ template, onDelete, onEdit }) => {
  return (
    <div className="relative bg-gray-900/90 rounded border border-gray-700 p-3 hover:shadow-lg hover:scale-105 shadow-lg cursor-pointer">
      <div className="absolute top-1 right-1 flex gap-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit(template);
          }}
          className="p-1 bg-red-900/50 rounded hover:bg-red-900/70 text-red-300 border border-red-800"
        >
          <Edit2 size={14} />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (confirm("Delete this template?")) {
              onDelete(template.id);
            }
          }}
          className="p-1 bg-red-900 rounded hover:bg-red-800 text-white"
        >
          <Trash2 size={14} />
        </button>
      </div>

      <div className="mb-2">
        <h4 className="text-sm font-bold text-white">
          {template.templateName}
        </h4>
        <p className="text-xs text-gray-400 capitalize">{template.category}</p>
      </div>

      <div className="mb-2">
        <span className="text-xs text-yellow-400 font-semibold">
          {formatCost(template.cost, template.costCurrency)}
        </span>
      </div>
    </div>
  );
};

const TemplatesTab: React.FC<TemplatesTabProps> = ({
  warband,
  setWarband,
  onAddModel,
  items,
  setItems,
  models,
  openModelTemplateEditor,
  openItemTemplateEditor,
  modelTemplates,
  itemTemplates,
  updateModelTemplates,
  updateItemTemplates,
}) => {
  const [mainTab, setMainTab] = useState<
    "models" | "items" | "import"
  >("models");
  const [importTab, setImportTab] = useState<"vanilla" | "homebrew">("vanilla");
  const [selectedHomebrewSet, setSelectedHomebrewSet] = useState<string | null>(
    null
  );
  const [selectedFaction, setSelectedFaction] = useState<string>("general");
  const [selectedFactionVariant, setSelectedFactionVariant] = useState<string | null>(null);

  // Ensure templates have IDs when component mounts
  useEffect(() => {
    // Fix any templates that don't have IDs (backwards compatibility)
    const fixedModels = modelTemplates.map((m: any) => ({
      ...m,
      id: m.id || generateId(),
    }));
    if (JSON.stringify(fixedModels) !== JSON.stringify(modelTemplates)) {
      updateModelTemplates(fixedModels);
    }
  }, [modelTemplates, updateModelTemplates]);

  const handleDeleteModelTemplate = (id: string) => {
    const updated = modelTemplates.filter((t) => t.id !== id);
    updateModelTemplates(updated);
  };

  const handleDeleteItemTemplate = (id: string) => {
    const updated = itemTemplates.filter((t) => t.id !== id);
    updateItemTemplates(updated);
  };

  const addHomebrewModel = (template: Omit<ModelTemplate, 'id' | 'templateName'>) => {
    const newTemplate: ModelTemplate = {
      id: generateId(),
      templateName: template.name + " (Homebrew)",
      ...template,
      itemTemplateIds: template.itemTemplateIds || [],
    };
    const updated = [...modelTemplates, newTemplate];
    updateModelTemplates(updated);
    alert(`${template.name} added from homebrew!`);
  };

  const addHomebrewItem = (template: Omit<ItemTemplate, 'id' | 'templateName'>) => {
    const newTemplate: ItemTemplate = {
      id: generateId(),
      templateName: template.name + " (Homebrew)",
      ...template,
    };
    const updated = [...itemTemplates, newTemplate];
    updateItemTemplates(updated);
    alert(`${template.name} added from homebrew!`);
  };

  const addEntireHomebrewPack = () => {
    const set = HOMEBREW_SETS.find((s) => s.name === selectedHomebrewSet);
    if (!set) return;

    let addedCount = 0;
    const newModelTemplates: ModelTemplate[] = [];
    const newItemTemplates: ItemTemplate[] = [];

    if (set.data.modelTemplates) {
      set.data.modelTemplates.forEach((template: any) => {
        const newTemplate: ModelTemplate = {
          id: generateId(),
          templateName: template.name + " (Homebrew)",
          ...template,
          itemTemplateIds: template.itemTemplateIds || [],
        };
        newModelTemplates.push(newTemplate);
        addedCount++;
      });
    }

    if (set.data.itemTemplates) {
      set.data.itemTemplates.forEach((template: any) => {
        const newTemplate: ItemTemplate = {
          id: generateId(),
          templateName: template.name + " (Homebrew)",
          ...template,
        };
        newItemTemplates.push(newTemplate);
        addedCount++;
      });
    }

    // Update templates in batches
    if (newModelTemplates.length > 0) {
      updateModelTemplates([...modelTemplates, ...newModelTemplates]);
    }
    if (newItemTemplates.length > 0) {
      updateItemTemplates([...itemTemplates, ...newItemTemplates]);
    }

    alert(`Added all ${addedCount} templates from "${selectedHomebrewSet}"!`);
  };

  const sortedElites = modelTemplates
    .filter((t) => t.type === "elite")
    .sort((a, b) => b.cost - a.cost || a.name.localeCompare(b.name));

  const sortedTroopers = modelTemplates
    .filter((t) => t.type === "trooper")
    .sort((a, b) => b.cost - a.cost || a.name.localeCompare(b.name));

  const categorizedItems = ITEM_CATEGORIES.map((category) => ({
    category,
    templates: itemTemplates
      .filter((t) => t.category === category)
      .sort((a, b) => b.cost - a.cost || a.name.localeCompare(b.name)),
  })).filter((group) => group.templates.length > 0);

  return (
    <div className="space-y-6">
      <div className="bg-gray-900/90 rounded border border-gray-700 p-4 flex justify-between items-center shadow-lg">
        <h2 className="text-3xl font-bold old-english text-yellow-500">
          {warband.templateSetName || "Templates"}
        </h2>
        <button
          onClick={() => {
            const newName = prompt(
              "Enter template set name:",
              warband.templateSetName || "Trench Pilgrims"
            );
            if (newName) {
              setWarband({ ...warband, templateSetName: newName });
            }
          }}
          className="bg-red-900 px-4 py-2 rounded hover:bg-red-800 flex items-center gap-2"
        >
          <Edit2 size={16} /> Edit Name
        </button>
      </div>

      <div className="flex gap-4">
        <button
          onClick={() => {
            const templatesData = {
              modelTemplates,
              itemTemplates,
            };
            const blob = new Blob([JSON.stringify(templatesData, null, 2)], {
              type: "application/json",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${warband.templateSetName || "Templates"}.json`;
            a.click();
          }}
          className="bg-red-900 text-white px-4 py-2 rounded hover:bg-red-800 flex items-center gap-2 tutorial-export-templates"
        >
          <Download size={16} /> Export Templates
        </button>

        <label className="bg-red-900 text-white px-4 py-2 rounded hover:bg-red-800 flex items-center gap-2 cursor-pointer">
          <Upload size={16} /> Import Templates
          <input
            type="file"
            accept=".json"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = (event) => {
                try {
                  const data = JSON.parse(event.target?.result as string);
                  if (data.modelTemplates) {
                    // Fix missing IDs on import
                    const fixedModels = data.modelTemplates.map((m: any) => ({
                      ...m,
                      id: m.id || generateId(),
                    }));
                    updateModelTemplates(fixedModels);
                  }
                  if (data.itemTemplates) {
                    updateItemTemplates(data.itemTemplates);
                  }
                  alert("Templates imported successfully!");
                } catch (err) {
                  alert("Error importing templates: " + (err as Error).message);
                }
              };
              reader.readAsText(file);
            }}
          />
        </label>
      </div>

      <div className="flex border-b border-gray-700 template-sub-tabs">
        <button
          onClick={() => setMainTab("models")}
          className={`px-6 py-3 font-semibold rounded-t-lg ${
            mainTab === "models"
              ? "bg-gray-900 bg-opacity-90 border-b-2 border-yellow-500 text-white"
              : "bg-gray-900 bg-opacity-70 text-gray-400 hover:text-white hover:bg-opacity-90"
          }`}
        >
          Model Templates
        </button>
        <button
          onClick={() => setMainTab("items")}
          className={`px-6 py-3 font-semibold rounded-t-lg ${
            mainTab === "items"
              ? "bg-gray-900 bg-opacity-90 border-b-2 border-yellow-500 text-white"
              : "bg-gray-900 bg-opacity-70 text-gray-400 hover:text-white hover:bg-opacity-90"
          }`}
        >
          Item Templates
        </button>
        <button
          onClick={() => setMainTab("import")}
          className={`px-6 py-3 font-semibold rounded-t-lg ${
            mainTab === "import"
              ? "bg-gray-900 bg-opacity-90 border-b-2 border-yellow-500 text-white"
              : "bg-gray-900 bg-opacity-70 text-gray-400 hover:text-white hover:bg-opacity-90"
          }`}
        >
          Import Premade Templates
        </button>
      </div>

      {mainTab === "models" && (
        <div className="space-y-6">
          <button
            onClick={() => openModelTemplateEditor()}
            className="w-full bg-red-900 text-white px-4 py-2 rounded hover:bg-red-800 flex items-center justify-center gap-2 tutorial-create-template"
          >
            <Plus size={16} /> Create New Model Template
          </button>

          <div>
            <h3 className="text-xl font-bold old-english text-yellow-400 mb-3">
              Elites ({sortedElites.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {sortedElites.map((template) => (
                <ModelTemplateCard
                  key={template.id}
                  template={template}
                  itemTemplates={itemTemplates}
                  onDelete={handleDeleteModelTemplate}
                  onEdit={() => openModelTemplateEditor(template)}
                />
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xl font-bold old-english text-gray-300 mb-3">
              Troopers ({sortedTroopers.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {sortedTroopers.map((template) => (
                <ModelTemplateCard
                  key={template.id}
                  template={template}
                  itemTemplates={itemTemplates}
                  onDelete={handleDeleteModelTemplate}
                  onEdit={() => openModelTemplateEditor(template)}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {mainTab === "items" && (
        <div className="space-y-6">
          <button
            onClick={() => openItemTemplateEditor()}
            className="w-full bg-red-900 text-white px-4 py-2 rounded hover:bg-red-800 flex items-center justify-center gap-2"
          >
            <Plus size={16} /> Create New Item Template
          </button>

          {categorizedItems.map(({ category, templates }) => (
            <div key={category}>
              <h3 className="text-xl font-bold old-english text-gray-300 mb-3 capitalize bg-gray-950 border-b border-gray-700 p-2">
                {category} ({templates.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {templates.map((template) => (
                  <ItemTemplateCard
                    key={template.id}
                    template={template}
                    onDelete={handleDeleteItemTemplate}
                    onEdit={() => openItemTemplateEditor(template)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {mainTab === "import" && (
        <div className="space-y-6">
          {/* Import Premade Templates Sub-tabs */}
          <div className="flex gap-4 border-b border-gray-700">
            <button
              onClick={() => {
                setImportTab("vanilla");
                setSelectedFaction("general");
                setSelectedFactionVariant(null);
              }}
              className={`px-6 py-3 font-semibold rounded-t-lg ${
                importTab === "vanilla"
                  ? "bg-gray-900 bg-opacity-90 border-b-2 border-yellow-500 text-white"
                  : "bg-gray-900 bg-opacity-70 text-gray-400 hover:text-white hover:bg-opacity-90"
              }`}
            >
              Vanilla Factions
            </button>
            <button
              onClick={() => {
                setImportTab("homebrew");
                setSelectedHomebrewSet(null);
              }}
              className={`px-6 py-3 font-semibold rounded-t-lg ${
                importTab === "homebrew"
                  ? "bg-gray-900 bg-opacity-90 border-b-2 border-yellow-500 text-white"
                  : "bg-gray-900 bg-opacity-70 text-gray-400 hover:text-white hover:bg-opacity-90"
              }`}
            >
              Homebrew
            </button>
          </div>

          {importTab === "vanilla" && (
            <VanillaFactionsSection
              selectedFaction={selectedFaction}
              setSelectedFaction={setSelectedFaction}
              selectedFactionVariant={selectedFactionVariant}
              setSelectedFactionVariant={setSelectedFactionVariant}
              modelTemplates={modelTemplates}
              itemTemplates={itemTemplates}
              updateModelTemplates={updateModelTemplates}
              updateItemTemplates={updateItemTemplates}
              onAddModel={onAddModel}
              warband={warband}
              setWarband={setWarband}
              models={models}
              items={items}
            />
          )}

          {importTab === "homebrew" && (
            <HomebrewSection
              selectedHomebrewSet={selectedHomebrewSet}
              setSelectedHomebrewSet={setSelectedHomebrewSet}
          modelTemplates={modelTemplates}
              itemTemplates={itemTemplates}
          updateModelTemplates={updateModelTemplates}
              updateItemTemplates={updateItemTemplates}
            />
          )}
        </div>
      )}

    </div>
  );
};

// General Armoury Section Component (universal items only)
const GeneralArmourySection: React.FC<{
  itemTemplates: ItemTemplate[];
  updateItemTemplates: (templates: ItemTemplate[]) => void;
}> = ({
  itemTemplates,
  updateItemTemplates,
}) => {
  const addGeneralItem = (item: any) => {
    const newTemplate = {
      id: generateId(),
      templateName: item.name + " (General)",
      ...item,
    };
    const updated = [...itemTemplates, newTemplate];
    updateItemTemplates(updated);
    alert(`${item.name} added to item templates!`);
  };

  const generalByCategory = ITEM_CATEGORIES.map((category) => ({
    category,
    items: GENERAL_ARMOURY_ITEMS.filter((i: any) => i.category === category),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="space-y-6">
      <h3 className="text-2xl font-bold old-english text-yellow-400 mb-6">
        General Armoury - Universal Equipment
      </h3>

      <p className="text-gray-300 mb-6">
        These items are available to all factions in Trench Crusade.
      </p>

      {generalByCategory.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-400">No general armoury items found.</p>
        </div>
      ) : (
        generalByCategory.map(({ category, items }) => (
          <div key={category}>
            <h4 className="text-xl font-bold old-english text-gray-300 mb-3 capitalize">
              {category} ({items.length})
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {items.map((item: any) => (
                <div
                  key={item.name}
                  className="bg-gray-900/90 rounded border border-gray-700 p-4 hover:bg-gray-800/90 hover:shadow-lg hover:scale-105 shadow-lg cursor-pointer transition"
                  onClick={() => addGeneralItem(item)}
                >
                  <h5 className="font-bold text-white">{item.name}</h5>
                  <p className="text-xs text-yellow-400 mt-1">
                    {formatCost(item.cost || 0, item.costCurrency || "ducats")}
                  </p>
                  {item.notes && (
                    <p className="text-xs text-gray-300 mt-1 italic">
                      {item.notes}
                    </p>
                  )}
                  <div className="mt-3 text-right">
                    <button className="bg-red-900 text-white px-3 py-1 rounded text-sm hover:bg-red-800">
                      Add to Templates
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// Vanilla Factions Section Component
const VanillaFactionsSection: React.FC<{
  selectedFaction: string;
  setSelectedFaction: (faction: string) => void;
  selectedFactionVariant: string | null;
  setSelectedFactionVariant: (variant: string | null) => void;
  modelTemplates: ModelTemplate[];
  itemTemplates: ItemTemplate[];
  updateModelTemplates: (templates: ModelTemplate[]) => void;
  updateItemTemplates: (templates: ItemTemplate[]) => void;
  onAddModel?: (data: { model: any; newItems: any[] }) => void;
  warband?: Warband;
  setWarband?: (warband: Warband) => void;
  models?: any[];
  items?: any[];
}> = ({
  selectedFaction,
  setSelectedFaction,
  selectedFactionVariant,
  setSelectedFactionVariant,
  modelTemplates,
  itemTemplates,
  updateModelTemplates,
  updateItemTemplates,
  onAddModel,
  warband,
  setWarband,
  models,
  items,
}) => {
  const addPremadeItem = (item: any) => {
    const newTemplate = {
      id: generateId(),
      templateName: item.name + " (Vanilla)",
      ...item,
    };
    const updated = [...itemTemplates, newTemplate];
    updateItemTemplates(updated);
    alert(`${item.name} added to item templates!`);
  };

  const addPremadeModel = (model: any) => {
    const newTemplate = {
      id: generateId(),
      templateName: model.name + " (Vanilla)",
      ...model,
      itemTemplateIds: model.itemTemplateIds || [],
    };
    const updated = [...modelTemplates, newTemplate];
    updateModelTemplates(updated);
    alert(`${model.name} added to model templates!`);
  };

  // Special handling for mercenaries - ONLY adds to templates, does not hire into warband
  const addMercenary = (mercenary: any) => {
    if (!updateModelTemplates || !updateItemTemplates) {
      alert("Cannot add mercenary: missing required props");
      return;
    }

    let templateAdded = 0;
    let itemTemplatesAdded = 0;

    // Check if mercenary model template exists
    const existingMercenaryTemplate = modelTemplates.find((mt) =>
      mt.name === mercenary.name && mt.class === mercenary.class
    );

    if (!existingMercenaryTemplate) {
      // Add mercenary to model templates
      const mercenaryTemplate = {
        id: generateId(),
        templateName: mercenary.name + " (Mercenary)",
        name: mercenary.name,
        class: mercenary.class,
        type: mercenary.type,
        cost: mercenary.cost,
        costCurrency: mercenary.costCurrency,
        stats: mercenary.stats,
        keywords: mercenary.keywords,
        abilities: mercenary.abilities,
        skills: mercenary.skills,
        itemTemplateIds: mercenary.itemTemplateIds || [],
        inbuiltEquipment: mercenary.inbuiltEquipment,
        upgrades: mercenary.upgrades,
        limit: mercenary.limit,
      };
      updateModelTemplates([...modelTemplates, mercenaryTemplate]);
      templateAdded = 1;
    }

    // Check and add missing item templates (regular equipment)
    const missingItemTemplates: any[] = [];

    mercenary.itemTemplateIds?.forEach((itemTemplateId: string) => {
      const itemTemplate = itemTemplates.find((it) => it.id === itemTemplateId);
      if (itemTemplate) {
        // Check if this item template is already loaded (by name and category)
        const existingTemplate = itemTemplates.find((it) =>
          it.name === itemTemplate.name && it.category === itemTemplate.category
        );

        if (!existingTemplate) {
          // Add the item template
          const newItemTemplate = {
            id: generateId(),
            templateName: itemTemplate.name + " (Mercenary)",
            name: itemTemplate.name,
            category: itemTemplate.category,
            keywords: itemTemplate.keywords,
            range: itemTemplate.range,
            armourModifier: itemTemplate.armourModifier,
            notes: itemTemplate.notes,
            cost: itemTemplate.cost,
            costCurrency: itemTemplate.costCurrency,
            limit: itemTemplate.limit,
            restrictions: itemTemplate.restrictions,
          };
          missingItemTemplates.push(newItemTemplate);
        }
      }
    });

    // Inbuilt equipment doesn't need separate templates since it's built into the model

    // Add missing item templates
    if (missingItemTemplates.length > 0) {
      updateItemTemplates([...itemTemplates, ...missingItemTemplates]);
      itemTemplatesAdded = missingItemTemplates.length;
    }

    alert(`${mercenary.name} mercenary template added! ${templateAdded > 0 ? 'Model template added. ' : ''}${itemTemplatesAdded > 0 ? `${itemTemplatesAdded} item template(s) added.` : 'All required item templates were already available.'}`);
  };

  const factions = ["general", ...Object.keys(FACTIONS)];

  // Get faction-specific data using new structured format
  const getFactionData = (factionName: string, variantName: string = "Standard") => {
    const faction = FACTIONS[factionName];
    if (!faction) return { models: [], items: [] };

    const variant = faction.variants[variantName];
    if (!variant) return { models: [], items: [] };

    return {
      models: variant.models || [],
      items: variant.items || []
    };
  };

  // Get available variants for a faction
  const getFactionVariants = (factionName: string): string[] => {
    const faction = FACTIONS[factionName];
    return faction ? Object.keys(faction.variants) : [];
  };

  // Get faction variants dynamically from the FACTIONS structure
  const getFactionVariantsRecord = (): Record<string, string[]> => {
    const variants: Record<string, string[]> = {};
    Object.keys(FACTIONS).forEach(factionName => {
      variants[factionName] = getFactionVariants(factionName);
    });
    return variants;
  };

  const factionVariants = getFactionVariantsRecord();

  const factionData = getFactionData(
    selectedFaction,
    selectedFactionVariant || getFactionVariants(selectedFaction)[0] || "Standard"
  );

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold old-english text-yellow-500 mb-6">
        Vanilla Factions
      </h2>

      {/* Faction Selection Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-700 mb-6">
        {factions.map((faction) => (
          <button
            key={faction}
            onClick={() => {
              setSelectedFaction(faction);
              setSelectedFactionVariant(null);
            }}
            className={`px-6 py-3 font-semibold rounded-t-lg ${
              selectedFaction === faction
                ? "bg-gray-900 bg-opacity-90 border-b-2 border-yellow-500 text-white"
                : "bg-gray-900 bg-opacity-70 text-gray-400 hover:text-white hover:bg-opacity-90"
            }`}
          >
            {faction === "general" ? "General Armoury" : faction}
          </button>
        ))}
      </div>

      {/* Faction Variant Tabs (if applicable) */}
      {selectedFaction !== "general" && factionVariants[selectedFaction] && factionVariants[selectedFaction].length > 1 && (
        <div className="flex flex-wrap gap-2 border-b border-gray-700 mb-6">
          {factionVariants[selectedFaction].map((variant) => (
            <button
              key={variant}
              onClick={() => setSelectedFactionVariant(variant === "Standard" ? null : variant)}
              className={`px-6 py-3 font-semibold rounded-t-lg ${
                (selectedFactionVariant === null && variant === "Standard") || selectedFactionVariant === variant
                  ? "bg-gray-900 bg-opacity-90 border-b-2 border-yellow-500 text-white"
                  : "bg-gray-900 bg-opacity-70 text-gray-400 hover:text-white hover:bg-opacity-90"
              }`}
            >
              {variant}
            </button>
          ))}
        </div>
      )}

      {/* Content based on selection */}
      {selectedFaction === "general" && (
        <GeneralArmourySection
          itemTemplates={itemTemplates}
          updateItemTemplates={updateItemTemplates}
        />
      )}

      {selectedFaction !== "general" && !FACTIONS[selectedFaction] && (
        <div className="text-center py-8">
          <p className="text-gray-400 mb-4">
            {selectedFaction} faction data not found.
          </p>
          <p className="text-gray-500 text-sm">
            Check that the faction is properly defined in the FACTIONS object.
          </p>
        </div>
      )}

      {selectedFaction !== "general" && FACTIONS[selectedFaction] && !factionData.models.length && !factionData.items.length && (
        <div className="text-center py-8">
          <p className="text-gray-400 mb-4">
            {selectedFaction} {selectedFactionVariant || "Standard"} templates not yet implemented.
          </p>
          <p className="text-gray-500 text-sm">
            This section is prepared for vanilla faction templates and variants.
          </p>
        </div>
      )}

      {selectedFaction !== "general" && (factionData.models.length > 0 || factionData.items.length > 0) && (
        <div className="space-y-10">
          {/* Models Section */}
          {factionData.models.length > 0 && (
            <div>
              <h3 className="text-2xl font-bold old-english text-yellow-400 mb-4">
                {selectedFaction === "Mercenaries" ? `Mercenaries (${factionData.models.length})` : `Models (${factionData.models.length})`}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {factionData.models.map((model: any) => (
                  <div
                    key={model.name}
                    className="bg-gray-900/90 rounded border border-gray-700 p-4 hover:bg-gray-800/90 hover:shadow-lg hover:scale-105 shadow-lg cursor-pointer transition"
                    onClick={() => selectedFaction === "Mercenaries" ? addMercenary(model) : addPremadeModel(model)}
                  >
                    <h4 className="font-bold text-white">{model.name}</h4>
                    <p className="text-xs text-gray-400">
                      {model.class} • {model.type.toUpperCase()}
                    </p>
                    <p className="text-xs text-yellow-400 mt-1">
                      {formatCost(model.cost, model.costCurrency)}
                    </p>

                    {/* Stats */}
                    <div className="mt-2 text-xs">
                      <div className="grid grid-cols-5 gap-1 text-center">
                        <div>
                          <div className="text-gray-300">MV</div>
                          <div className="text-white font-bold">{model.stats.movement}</div>
                        </div>
                        <div>
                          <div className="text-gray-300">ML</div>
                          <div className="text-white font-bold">{model.stats.melee}</div>
                        </div>
                        <div>
                          <div className="text-gray-300">RG</div>
                          <div className="text-white font-bold">{model.stats.ranged}</div>
                        </div>
                        <div>
                          <div className="text-gray-300">AR</div>
                          <div className="text-white font-bold">{model.stats.baseArmour}</div>
                        </div>
                        <div>
                          <div className="text-gray-300">HP</div>
                          <div className="text-white font-bold">{model.stats.finalArmour}</div>
                        </div>
                      </div>
                    </div>

                    {/* Keywords */}
                    {model.keywords.length > 0 && (
                      <div className="mt-2">
                        <div className="text-xs text-gray-300 mb-1">Keywords:</div>
                        <div className="flex flex-wrap gap-1">
                          {model.keywords.map((keyword: string, idx: number) => (
                            <span key={idx} className="text-xs bg-gray-700 text-yellow-300 px-2 py-1 rounded">
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Abilities */}
                    {model.abilities.length > 0 && (
                      <div className="mt-2">
                        <div className="text-xs text-gray-300 mb-1">Abilities:</div>
                        <div className="text-xs text-gray-200 max-h-20 overflow-y-auto">
                          {Array.isArray(model.abilities) && model.abilities.map((ability: string | { name: string; description: string }, idx: number) => (
                            <div key={idx} className="mb-1">
                              {typeof ability === 'string' ? ability : `${ability.name}: ${ability.description}`}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Built-in Equipment */}
                    {model.itemTemplateIds && model.itemTemplateIds.length > 0 && (
                      <div className="mt-2">
                        <div className="text-xs text-gray-300 mb-1">Built-in Equipment:</div>
                        <div className="text-xs text-green-300">
                          {model.itemTemplateIds.length} item(s) included
                        </div>
                      </div>
                    )}

                    {/* Upgrades */}
                    {model.upgrades && model.upgrades.length > 0 && (
                      <div className="mt-2">
                        <div className="text-xs text-gray-300 mb-1">Available Upgrades:</div>
                        <div className="text-xs text-purple-300 max-h-16 overflow-y-auto">
                          {model.upgrades.map((upgrade: any, idx: number) => (
                            <div key={idx} className="mb-1">
                              {upgrade.name} ({upgrade.cost ? `${upgrade.cost}👑` : 'Free'})
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-3 text-right">
                      <button className="bg-red-900 text-white px-3 py-1 rounded text-sm hover:bg-red-800">
                        {selectedFaction === "Mercenaries" ? "Add Mercenary Template" : "Add Model Template"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Items Section - Skip for Mercenaries */}
          {selectedFaction !== "Mercenaries" && ITEM_CATEGORIES.map((category) => {
            const items = factionData.items.filter(
              (i: any) => i.category === category
            );
            if (items.length === 0) return null;

            return (
              <div key={category}>
                <h3 className="text-xl font-bold old-english text-gray-300 mb-4 capitalize">
                  {category} ({items.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((item: any) => (
                    <div
                      key={item.name}
                      className="bg-gray-900/90 rounded border border-gray-700 p-4 hover:bg-gray-800/90 hover:shadow-lg hover:scale-105 shadow-lg cursor-pointer transition"
                      onClick={() => addPremadeItem(item)}
                    >
                      <h4 className="font-bold text-white">{item.name}</h4>
                      <p className="text-xs text-gray-400 capitalize">{item.category}</p>
                      <p className="text-xs text-yellow-400 mt-1">
                        {formatCost(item.cost, item.costCurrency)}
                      </p>

                      {/* Keywords */}
                      {item.keywords && item.keywords.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs text-gray-300 mb-1">Keywords:</div>
                          <div className="flex flex-wrap gap-1">
                            {item.keywords.map((keyword: string, idx: number) => (
                              <span key={idx} className="text-xs bg-gray-700 text-blue-300 px-2 py-1 rounded">
                                {keyword}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Range */}
                      {item.range && (
                        <div className="mt-2">
                          <div className="text-xs text-gray-300">Range: <span className="text-white">{item.range}</span></div>
                        </div>
                      )}

                      {/* Armour Modifier */}
                      {item.armourModifier && (
                        <div className="mt-1">
                          <div className="text-xs text-gray-300">Armour: <span className="text-white">{item.armourModifier}</span></div>
                        </div>
                      )}

                      {/* Notes */}
                      {item.notes && (
                        <div className="mt-2">
                          <div className="text-xs text-gray-300 mb-1">Notes:</div>
                          <p className="text-xs text-gray-200 italic max-h-16 overflow-y-auto">
                            {item.notes}
                          </p>
                        </div>
                      )}

                      <div className="mt-3 text-right">
                        <button className="bg-red-900 text-white px-3 py-1 rounded text-sm hover:bg-red-800">
                          Add Item
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Homebrew Section Component
const HomebrewSection: React.FC<{
  selectedHomebrewSet: string | null;
  setSelectedHomebrewSet: (set: string | null) => void;
  modelTemplates: ModelTemplate[];
  itemTemplates: ItemTemplate[];
  updateModelTemplates: (templates: ModelTemplate[]) => void;
  updateItemTemplates: (templates: ItemTemplate[]) => void;
}> = ({
  selectedHomebrewSet,
  setSelectedHomebrewSet,
  modelTemplates,
  itemTemplates,
  updateModelTemplates,
  updateItemTemplates,
}) => {
  const addHomebrewModel = (template: Omit<ModelTemplate, 'id' | 'templateName'>) => {
    const newTemplate: ModelTemplate = {
      id: generateId(),
      templateName: template.name + " (Homebrew)",
      ...template,
      itemTemplateIds: template.itemTemplateIds || [],
    };
    const updated = [...modelTemplates, newTemplate];
    updateModelTemplates(updated);
    alert(`${template.name} added from homebrew!`);
  };

  const addHomebrewItem = (template: Omit<ItemTemplate, 'id' | 'templateName'>) => {
    const newTemplate: ItemTemplate = {
      id: generateId(),
      templateName: template.name + " (Homebrew)",
      ...template,
    };
    const updated = [...itemTemplates, newTemplate];
    updateItemTemplates(updated);
    alert(`${template.name} added from homebrew!`);
  };

  const addEntireHomebrewPack = () => {
    const set = HOMEBREW_SETS.find((s) => s.name === selectedHomebrewSet);
    if (!set) return;

    let addedCount = 0;
    const newModelTemplates: ModelTemplate[] = [];
    const newItemTemplates: ItemTemplate[] = [];

    if (set.data.modelTemplates) {
      set.data.modelTemplates.forEach((template: any) => {
        const newTemplate: ModelTemplate = {
          id: generateId(),
          templateName: template.name + " (Homebrew)",
          ...template,
          itemTemplateIds: template.itemTemplateIds || [],
        };
        newModelTemplates.push(newTemplate);
        addedCount++;
      });
    }

    if (set.data.itemTemplates) {
      set.data.itemTemplates.forEach((template: any) => {
        const newTemplate: ItemTemplate = {
          id: generateId(),
          templateName: template.name + " (Homebrew)",
          ...template,
        };
        newItemTemplates.push(newTemplate);
        addedCount++;
      });
    }

    // Update templates in batches
    if (newModelTemplates.length > 0) {
      updateModelTemplates([...modelTemplates, ...newModelTemplates]);
    }
    if (newItemTemplates.length > 0) {
      updateItemTemplates([...itemTemplates, ...newItemTemplates]);
    }

    alert(`Added all ${addedCount} templates from "${selectedHomebrewSet}"!`);
  };

  return (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold old-english text-yellow-500 mb-6">
            Community Homebrew
          </h2>

          {HOMEBREW_SETS.length === 0 ? (
            <p className="text-gray-400">No community homebrew added yet.</p>
          ) : (
            <div>
              <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-700 pb-2">
                {HOMEBREW_SETS.map((set) => (
                  <button
                    key={set.name}
                    onClick={() => setSelectedHomebrewSet(set.name)}
                    className={`px-6 py-3 font-semibold rounded-t-lg ${
                      selectedHomebrewSet === set.name
                        ? "bg-gray-900 bg-opacity-90 border-b-2 border-yellow-500 text-white"
                        : "bg-gray-900 bg-opacity-70 text-gray-400 hover:text-white hover:bg-opacity-90"
                    }`}
                  >
                    {set.name}
                  </button>
                ))}
              </div>

              {selectedHomebrewSet && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-bold text-white">
                      {selectedHomebrewSet}
                    </h3>
                    <button
                      onClick={addEntireHomebrewPack}
                      className="bg-yellow-600 text-black px-6 py-3 rounded hover:bg-yellow-500 font-bold flex items-center gap-2"
                    >
                      Add Entire Pack
                    </button>
                  </div>

                  {/* Link */}
                  {(() => {
                    const selectedSet = HOMEBREW_SETS.find((s) => s.name === selectedHomebrewSet);
                    return selectedSet && selectedSet.link && (
                      <div className="mb-6 p-4 bg-blue-900/20 border border-blue-700 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-blue-400 font-semibold">🔗 Miniatures:</span>
                          <a
                            href={selectedSet.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-300 hover:text-blue-100 underline break-all"
                          >
                            {selectedSet.link}
                          </a>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Models */}
                  {(() => {
                    const selectedSet = HOMEBREW_SETS.find((s) => s.name === selectedHomebrewSet);
                    return selectedSet && selectedSet.data && selectedSet.data.modelTemplates && selectedSet.data.modelTemplates.length > 0 && (
                    <div className="mb-10">
                      <h4 className="text-xl font-bold text-gray-300 mb-4">
                        Models
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {selectedSet.data.modelTemplates.map((template: any) => (
                          <div
                            key={template.id || template.name}
                            className="bg-gray-900/90 rounded border border-gray-700 p-4 hover:bg-gray-800/90 shadow-lg cursor-pointer transition"
                            onClick={() => addHomebrewModel(template)}
                          >
                            <h5 className="font-bold text-white">
                              {template.name}
                            </h5>
                            <p className="text-xs text-gray-400">
                              {template.class} • {template.type.toUpperCase()}
                            </p>
                            <p className="text-xs text-yellow-400 mt-1">
                              {formatCost(template.cost, template.costCurrency)}
                            </p>

                            {/* Stats */}
                            {template.stats && (
                              <div className="mt-2 text-xs">
                                <div className="grid grid-cols-5 gap-1 text-center">
                                  <div>
                                    <div className="text-gray-300">MV</div>
                                    <div className="text-white font-bold">{template.stats.movement}</div>
                                  </div>
                                  <div>
                                    <div className="text-gray-300">ML</div>
                                    <div className="text-white font-bold">{template.stats.melee}</div>
                                  </div>
                                  <div>
                                    <div className="text-gray-300">RG</div>
                                    <div className="text-white font-bold">{template.stats.ranged}</div>
                                  </div>
                                  <div>
                                    <div className="text-gray-300">AR</div>
                                    <div className="text-white font-bold">{template.stats.baseArmour}</div>
                                  </div>
                                  <div>
                                    <div className="text-gray-300">HP</div>
                                    <div className="text-white font-bold">{template.stats.finalArmour}</div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Keywords */}
                            {template.keywords && template.keywords.length > 0 && (
                              <div className="mt-2">
                                <div className="text-xs text-gray-300 mb-1">Keywords:</div>
                                <div className="flex flex-wrap gap-1">
                                  {template.keywords.map((keyword: string, idx: number) => (
                                    <span key={idx} className="text-xs bg-gray-700 text-yellow-300 px-2 py-1 rounded">
                                      {keyword}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Abilities */}
                            {template.abilities && template.abilities.length > 0 && (
                              <div className="mt-2">
                                <div className="text-xs text-gray-300 mb-1">Abilities:</div>
                                <div className="text-xs text-gray-200 max-h-20 overflow-y-auto">
                                  {Array.isArray(template.abilities) && template.abilities.map((ability: any, idx: number) => (
                                    <div key={idx} className="mb-1">
                                      {typeof ability === 'string' ? ability : `${ability.name}: ${ability.description}`}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Built-in Equipment */}
                            {template.itemTemplateIds && template.itemTemplateIds.length > 0 && (
                              <div className="mt-2">
                                <div className="text-xs text-gray-300 mb-1">Built-in Equipment:</div>
                                <div className="text-xs text-green-300">
                                  {template.itemTemplateIds.length} item(s) included
                                </div>
                              </div>
                            )}

                            {/* Upgrades */}
                            {template.upgrades && template.upgrades.length > 0 && (
                              <div className="mt-2">
                                <div className="text-xs text-gray-300 mb-1">Available Upgrades:</div>
                                <div className="text-xs text-purple-300 max-h-16 overflow-y-auto">
                                  {template.upgrades.map((upgrade: any, idx: number) => (
                                    <div key={idx} className="mb-1">
                                      {upgrade.name} ({upgrade.cost ? `${upgrade.cost}👑` : 'Free'})
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="mt-3 text-right">
                              <button className="bg-red-900 text-white px-3 py-1 rounded text-sm hover:bg-red-800">
                                Add Model
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                  })()}

                  {/* Items */}
                  {(() => {
                    const selectedSet = HOMEBREW_SETS.find((s) => s.name === selectedHomebrewSet);
                    return selectedSet && selectedSet.data && selectedSet.data.itemTemplates && selectedSet.data.itemTemplates.length > 0 && (
                    <div>
                      <h4 className="text-xl font-bold text-gray-300 mb-4">
                        Items
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {selectedSet.data.itemTemplates.map((template: any) => (
                          <div
                            key={template.id || template.name}
                            className="bg-gray-900/90 rounded border border-gray-700 p-4 hover:bg-gray-800/90 shadow-lg cursor-pointer transition"
                            onClick={() => addHomebrewItem(template)}
                          >
                            <h5 className="font-bold text-white">
                              {template.name}
                            </h5>
                            <p className="text-xs text-gray-400 capitalize">{template.category}</p>
                            <p className="text-xs text-yellow-400 mt-1">
                              {formatCost(template.cost, template.costCurrency)}
                            </p>

                            {/* Keywords */}
                            {template.keywords && template.keywords.length > 0 && (
                              <div className="mt-2">
                                <div className="text-xs text-gray-300 mb-1">Keywords:</div>
                                <div className="flex flex-wrap gap-1">
                                  {template.keywords.map((keyword: string, idx: number) => (
                                    <span key={idx} className="text-xs bg-gray-700 text-blue-300 px-2 py-1 rounded">
                                      {keyword}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Range */}
                            {template.range && (
                              <div className="mt-2">
                                <div className="text-xs text-gray-300">Range: <span className="text-white">{template.range}</span></div>
                              </div>
                            )}

                            {/* Armour Modifier */}
                            {template.armourModifier && (
                              <div className="mt-1">
                                <div className="text-xs text-gray-300">Armour: <span className="text-white">{template.armourModifier}</span></div>
                              </div>
                            )}

                            {/* Notes */}
                            {template.notes && (
                              <div className="mt-2">
                                <div className="text-xs text-gray-300 mb-1">Notes:</div>
                                <p className="text-xs text-gray-200 italic max-h-16 overflow-y-auto">
                                  {template.notes}
                                </p>
                              </div>
                            )}

                            <div className="mt-3 text-right">
                              <button className="bg-red-900 text-white px-3 py-1 rounded text-sm hover:bg-red-800">
                                Add Item
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                  })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { TemplatesTab };
